// components/UserOverview.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardContent,
    Typography,
    Grid,
    Box,
    Divider,
    Skeleton,
    useTheme
} from '@mui/material';
import { useFetch } from '../../hooks';
import { transformToBTC } from '../../utils/helpers';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';

// Helper function to format BTC with USD value
const formatBTCWithUSD = (sats, btcPrice) => {
    if (sats === null || sats === undefined || isNaN(sats)) return 'N/A';

    const btcValue = transformToBTC(sats);

    if (!btcPrice || btcValue === 'N/A') {
        return btcValue;
    }

    // Extract numeric value from BTC string
    const btcNumeric = parseFloat(btcValue.replace(/[^\d.-]/g, ''));
    const isSats = btcValue.includes('SATS');

    let usdValue;
    if (isSats) {
        // Convert SATS to BTC then to USD
        usdValue = (btcNumeric / 100000000) * btcPrice;
    } else {
        // Already in BTC
        usdValue = btcNumeric * btcPrice;
    }

    if (usdValue < 0.01) {
        return btcValue; // Don't show USD for very small amounts
    }

    const formattedUSD = usdValue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'inherit' }}>
            <Typography variant="body2" sx={{ fontFamily: 'inherit', lineHeight: 1.2 }}>
                {btcValue}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1 }}>
                {formattedUSD}
            </Typography>
        </Box>
    );
};

// A little box for each stat, with a colored accent bar on the left
const StatBox = ({ label, value, accent }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.mode === 'light' ? '#f8f8f8' : theme.palette.grey[800],
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                boxShadow: 1,
                borderLeft: `4px solid ${ accent }`,
            }}
        >
            <Typography variant="subtitle2" color="text.secondary">
                {label}
            </Typography>
            <Box sx={{ fontWeight: 'bold', mt: 0.5 }}>
                {typeof value === 'string' || typeof value === 'number' ? (
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {value}
                    </Typography>
                ) : (
                    value
                )}
            </Box>
        </Box>
    );
};

const UserOverview = ({ userId }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { price: btcPrice } = useBitcoinPrice();
    const { data, loading, error } = useFetch(
        `/users/${ userId }/overview`,
        [userId]
    );

    if (loading) {
        return (
            <Skeleton
                variant="rectangular"
                width="100%"
                height={300}
                sx={{ borderRadius: 2 }}
            />
        );
    }

    if (error) {
        return (
            <Typography color="error" sx={{ m: 2 }}>
                Error: {error.message}
            </Typography>
        );
    }

    const {
        totalUniqueTokens,
        totalTrades,
        buyBTC,
        sellBTC,
        buyCount,
        sellCount,
        roi,
    } = data;

    const roiValue = Number(roi);
    const roiPositive = roiValue >= 0;
    const roiTextColor = roiPositive ? theme.palette.success.main : theme.palette.error.main;

    return (
        <Card sx={{
            borderRadius: 2,
            boxShadow: 3,
            mb: 4,
        }}>
            <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
                    {t('user.overview')}
                </Typography>

                {/* Stats Grid */}
                <Grid container spacing={2} mb={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatBox
                            label={t('user.tradedTokens')}
                            value={totalUniqueTokens}
                            accent={theme.palette.warning.main}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatBox
                            label={t('user.totalTrades')}
                            value={totalTrades}
                            accent={theme.palette.warning.main}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatBox
                            label={t('user.totalBuyVolume')}
                            value={formatBTCWithUSD(buyBTC, btcPrice)}
                            accent="#0288D1"  // blue
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatBox
                            label={t('user.totalSellVolume')}
                            value={formatBTCWithUSD(sellBTC, btcPrice)}
                            accent="#0288D1"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatBox
                            label={t('user.buyCount')}
                            value={buyCount}
                            accent="#9C27B0"  // purple
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatBox
                            label={t('user.sellCount')}
                            value={sellCount}
                            accent="#9C27B0"
                        />
                    </Grid>
                </Grid>

                {/* ROI Panel */}
                <Box
                    sx={{
                        backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : theme.palette.grey[900],
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        boxShadow: 1,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 500, color: roiTextColor }}
                        gutterBottom
                    >
                        {t('user.returnOnInvestment')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography
                        variant="h4"
                        sx={{ fontWeight: 'bold', color: roiTextColor }}
                    >
                        {roiValue.toFixed(2)}%{' '}
                        <Typography
                            component="span"
                            variant="subtitle1"
                            color="text.secondary"
                        >
                            ({transformToBTC(sellBTC - buyBTC)})
                        </Typography>
                    </Typography>
                    {btcPrice && (
                        <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {(() => {
                                const profitLossBTC = sellBTC - buyBTC;
                                const btcValue = transformToBTC(profitLossBTC);
                                const btcNumeric = parseFloat(btcValue.replace(/[^\d.-]/g, ''));
                                const isSats = btcValue.includes('SATS');

                                let usdValue;
                                if (isSats) {
                                    usdValue = (btcNumeric / 100000000) * btcPrice;
                                } else {
                                    usdValue = btcNumeric * btcPrice;
                                }

                                return usdValue.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                });
                            })()}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default UserOverview;
